<template>
  <div class="org-topology">
    <!-- 左侧面板 -->
    <div class="org-sidebar">
      <div class="sidebar-title">🏢 组织架构</div>

      <div class="sidebar-section">
        <div class="section-label">新增部门</div>
        <el-input v-model="newDeptName" size="mini" placeholder="部门名称" @keyup.enter.native="addRootDept" />
        <el-button size="mini" type="primary" style="margin-top:6px;width:100%" @click="addRootDept">+ 添加部门</el-button>
      </div>

      <div class="sidebar-section">
        <div class="section-label">未分配员工</div>
        <div class="unassigned-list">
          <div v-for="bot in unassignedBots" :key="bot.id" class="bot-chip"
            draggable="true" @dragstart="onBotDragStart($event, bot)">
            <span>{{ getEmoji(bot) }}</span>
            <span class="bot-chip-name">{{ bot.nickName }}</span>
          </div>
          <div v-if="unassignedBots.length === 0" class="no-unassigned">全部已分配 ✓</div>
        </div>
      </div>

      <div class="sidebar-tips">
        <div>💡 拖拽员工到部门节点</div>
        <div>🖱 右键部门可操作</div>
        <div>🔄 自动布局排列</div>
      </div>
    </div>

    <!-- 画布 -->
    <div class="org-canvas-wrap" @dragover.prevent @drop="onCanvasDrop">
      <div ref="container" class="org-canvas"></div>
      <div v-if="departments.length === 0" class="canvas-empty">
        <div style="font-size:40px">🏢</div>
        <div>在左侧添加部门，开始构建组织架构</div>
      </div>
    </div>

    <!-- 右键菜单 -->
    <div v-if="ctxMenu.visible" class="ctx-menu" :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }">
      <div class="ctx-item" @click="ctxRename">✏️ 重命名</div>
      <div class="ctx-item" @click="ctxAddChild">➕ 添加子部门</div>
      <div class="ctx-item ctx-danger" @click="ctxDelete">🗑 删除部门</div>
    </div>
  </div>
</template>

<script>
import { Graph, Dom } from '@antv/x6';
import dagre from 'dagre';

const COLORS = ['#5F95FF','#8b5cf6','#ec4899','#f97316','#22c55e','#14b8a6','#e11d48','#eab308'];
const EMOJIS = ['🤖','🧠','⚡','🔧','📊','💬','🎯','🛡️','📝','🔍','🚀','🎨'];

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) { h = ((h << 5) - h) + str.charCodeAt(i); h |= 0; }
  return Math.abs(h);
}

const STORAGE_KEY = 'agent_org_data_v2';

// 注册部门节点
Graph.registerNode('dept-node', {
  width: 200, height: 56,
  markup: [
    { tagName: 'rect', attrs: { class: 'card' } },
    { tagName: 'text', attrs: { class: 'dept-name' } },
    { tagName: 'g', attrs: { class: 'btn add-child' }, children: [
      { tagName: 'circle', attrs: { class: 'add-child' } },
      { tagName: 'text', attrs: { class: 'add-child' } },
    ]},
  ],
  attrs: {
    '.card': { refWidth: '100%', refHeight: '100%', rx: 10, ry: 10, fill: '#5F95FF', stroke: '#5F95FF', strokeWidth: 1 },
    '.dept-name': { refX: 0.5, refY: 0.5, fill: '#fff', fontSize: 14, fontWeight: 600, textAnchor: 'middle', textVerticalAnchor: 'middle' },
    '.btn.add-child': { refDx: -16, refY: 8, event: 'dept:add-child' },
    '.btn > circle': { r: 9, fill: 'transparent', stroke: '#fff', strokeWidth: 1 },
    '.btn.add-child > text': { fontSize: 18, fontWeight: 800, fill: '#fff', x: -5, y: 6, fontFamily: 'Times New Roman', text: '+' },
  },
}, true);

// 注册员工节点
Graph.registerNode('bot-node', {
  width: 160, height: 72,
  markup: [
    { tagName: 'rect', attrs: { class: 'card' } },
    { tagName: 'text', attrs: { class: 'emoji' } },
    { tagName: 'text', attrs: { class: 'bot-name' } },
    { tagName: 'text', attrs: { class: 'bot-sub' } },
  ],
  attrs: {
    '.card': { refWidth: '100%', refHeight: '100%', rx: 10, ry: 10, fill: '#fff', stroke: '#5F95FF', strokeWidth: 1.5 },
    '.emoji': { x: 16, y: 28, fontSize: 22, textVerticalAnchor: 'middle' },
    '.bot-name': { x: 48, y: 26, fill: '#1a1a2e', fontSize: 13, fontWeight: 600 },
    '.bot-sub': { x: 48, y: 46, fill: '#9ca3af', fontSize: 11 },
  },
}, true);

// 注册边
Graph.registerEdge('org-edge', {
  zIndex: -1,
  attrs: { line: { strokeWidth: 2, stroke: '#A2B1C3', sourceMarker: null, targetMarker: null } },
}, true);

export default {
  name: 'AgentOrgTopology',
  props: { bots: { type: Array, default: () => [] } },
  data() {
    return {
      graph: null,
      departments: [],   // { id, name, parentId }
      assignments: {},   // { botId: deptId }
      newDeptName: '',
      ctxMenu: { visible: false, x: 0, y: 0, deptId: null },
      draggingBot: null,
    };
  },
  computed: {
    unassignedBots() {
      const assigned = new Set(Object.keys(this.assignments));
      return this.bots.filter(b => !assigned.has(String(b.id)));
    },
  },
  mounted() {
    this.loadData();
    this.$nextTick(() => this.initGraph());
    document.addEventListener('click', this.hideCtxMenu);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.hideCtxMenu);
    if (this.graph) this.graph.dispose();
  },
  watch: {
    bots() { this.renderGraph(); },
  },
  methods: {
    loadData() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) { const d = JSON.parse(raw); this.departments = d.departments || []; this.assignments = d.assignments || {}; }
      } catch (e) { console.warn('loadData', e); }
    },
    saveData() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ departments: this.departments, assignments: this.assignments }));
    },

    initGraph() {
      if (!this.$refs.container) return;
      this.graph = new Graph({
        container: this.$refs.container,
        width: this.$refs.container.offsetWidth,
        height: this.$refs.container.offsetHeight,
        background: { color: '#f7f8fa' },
        grid: { visible: true, size: 20, type: 'dot', args: { color: '#d1d5db' } },
        mousewheel: { enabled: true, modifiers: ['ctrl', 'meta'] },
        panning: { enabled: true },
        interacting: false,  // 禁止拖动节点，布局由 dagre 控制
      });

      // 部门节点上的 + 按钮
      this.graph.on('dept:add-child', ({ node }) => {
        const deptId = node.getData().id;
        this.$prompt('请输入子部门名称', '添加子部门', { confirmButtonText: '确定', cancelButtonText: '取消' })
          .then(({ value }) => {
            if (!value || !value.trim()) return;
            this.departments.push({ id: 'dept-' + Date.now(), name: value.trim(), parentId: deptId });
            this.saveData(); this.renderGraph();
          }).catch(() => {});
      });

      // 右键菜单（部门节点）
      this.graph.on('node:contextmenu', ({ node, e }) => {
        if (node.getData()?.type !== 'dept') return;
        e.preventDefault();
        this.ctxMenu = { visible: true, x: e.clientX, y: e.clientY, deptId: node.getData().id };
      });

      this.renderGraph();
    },

    renderGraph() {
      if (!this.graph) return;
      this.graph.clearCells();
      if (this.departments.length === 0) return;

      const cells = [];

      // 部门节点
      this.departments.forEach(dept => {
        const color = COLORS[hashCode(dept.id) % COLORS.length];
        cells.push(this.graph.createNode({
          id: 'dept-' + dept.id,
          shape: 'dept-node',
          attrs: {
            '.card': { fill: color, stroke: color },
            '.dept-name': { text: Dom.breakText(dept.name, { width: 160, height: 40 }) },
          },
          data: { type: 'dept', id: dept.id },
        }));
      });

      // 员工节点
      this.bots.forEach(bot => {
        const deptId = this.assignments[String(bot.id)];
        if (!deptId) return;
        const color = COLORS[hashCode(String(bot.id)) % COLORS.length];
        cells.push(this.graph.createNode({
          id: 'bot-' + bot.id,
          shape: 'bot-node',
          attrs: {
            '.card': { stroke: color },
            '.emoji': { text: EMOJIS[hashCode(String(bot.id)) % EMOJIS.length] },
            '.bot-name': { text: bot.nickName.length > 8 ? bot.nickName.slice(0, 8) + '…' : bot.nickName },
            '.bot-sub': { text: 'Bot · ' + (bot.openclawAgentId || '').slice(0, 8) },
          },
          data: { type: 'bot', id: bot.id, deptId },
        }));
      });

      // 部门层级边
      this.departments.forEach(dept => {
        if (!dept.parentId) return;
        cells.push(this.graph.createEdge({
          shape: 'org-edge',
          source: { cell: 'dept-' + dept.parentId },
          target: { cell: 'dept-' + dept.id },
        }));
      });

      // 员工 → 部门边
      this.bots.forEach(bot => {
        const deptId = this.assignments[String(bot.id)];
        if (!deptId) return;
        cells.push(this.graph.createEdge({
          shape: 'org-edge',
          source: { cell: 'dept-' + deptId },
          target: { cell: 'bot-' + bot.id },
          attrs: { line: { stroke: '#d1d5db', strokeWidth: 1, strokeDasharray: '4 3' } },
        }));
      });

      this.graph.resetCells(cells);
      this.layout();
    },

    layout() {
      const nodes = this.graph.getNodes();
      const edges = this.graph.getEdges();
      const g = new dagre.graphlib.Graph();
      g.setGraph({ rankdir: 'TB', nodesep: 24, ranksep: 40 });
      g.setDefaultEdgeLabel(() => ({}));

      nodes.forEach(node => {
        const size = node.getSize();
        g.setNode(node.id, { width: size.width, height: size.height });
      });
      edges.forEach(edge => {
        const src = edge.getSource();
        const tgt = edge.getTarget();
        if (src.cell && tgt.cell) g.setEdge(src.cell, tgt.cell);
      });

      dagre.layout(g);

      this.graph.freeze();
      g.nodes().forEach(id => {
        const node = this.graph.getCellById(id);
        if (node) { const pos = g.node(id); node.position(pos.x - pos.width / 2, pos.y - pos.height / 2); }
      });
      this.graph.unfreeze();
      this.graph.zoomToFit({ padding: 40 });
    },

    // ─── 部门操作 ─────────────────────────────────────────
    addRootDept() {
      const name = this.newDeptName;
      if (!name || !name.trim()) return;
      this.departments.push({ id: 'dept-' + Date.now(), name: name.trim(), parentId: null });
      this.newDeptName = '';
      this.saveData(); this.renderGraph();
    },

    hideCtxMenu() { this.ctxMenu.visible = false; },

    ctxRename() {
      const deptId = this.ctxMenu.deptId;
      const dept = this.departments.find(d => d.id === deptId);
      if (!dept) return;
      this.$prompt('请输入新名称', '重命名部门', { inputValue: dept.name, confirmButtonText: '确定', cancelButtonText: '取消' })
        .then(({ value }) => { if (value && value.trim()) { dept.name = value.trim(); this.saveData(); this.renderGraph(); } })
        .catch(() => {});
      this.hideCtxMenu();
    },

    ctxAddChild() {
      const deptId = this.ctxMenu.deptId;
      this.$prompt('请输入子部门名称', '添加子部门', { confirmButtonText: '确定', cancelButtonText: '取消' })
        .then(({ value }) => {
          if (value && value.trim()) {
            this.departments.push({ id: 'dept-' + Date.now(), name: value.trim(), parentId: deptId });
            this.saveData(); this.renderGraph();
          }
        }).catch(() => {});
      this.hideCtxMenu();
    },

    ctxDelete() {
      const deptId = this.ctxMenu.deptId;
      this.$confirm('删除部门后，其成员将变为未分配状态', '确认删除', { type: 'warning' })
        .then(() => {
          const toDelete = new Set();
          const collect = id => { toDelete.add(id); this.departments.filter(d => d.parentId === id).forEach(d => collect(d.id)); };
          collect(deptId);
          this.departments = this.departments.filter(d => !toDelete.has(d.id));
          Object.keys(this.assignments).forEach(botId => { if (toDelete.has(this.assignments[botId])) this.$delete(this.assignments, botId); });
          this.saveData(); this.renderGraph();
        }).catch(() => {});
      this.hideCtxMenu();
    },

    // ─── 拖拽员工 ─────────────────────────────────────────
    onBotDragStart(e, bot) {
      this.draggingBot = bot;
      e.dataTransfer.setData('botId', String(bot.id));
    },

    onCanvasDrop(e) {
      if (!this.draggingBot || !this.graph) return;
      const p = this.graph.clientToLocal(e.clientX, e.clientY);
      const deptNode = this.graph.getNodes().find(n => {
        if (n.getData()?.type !== 'dept') return false;
        return n.getBBox().containsPoint(p);
      });
      const bot = this.draggingBot;
      this.draggingBot = null;
      if (deptNode) {
        this.$set(this.assignments, String(bot.id), deptNode.getData().id);
        this.saveData(); this.renderGraph();
      }
    },

    getEmoji(bot) { return EMOJIS[hashCode(String(bot.id)) % EMOJIS.length]; },
  },
};
</script>

<style lang="scss" scoped>
.org-topology { display: flex; height: 100%; position: relative; overflow: hidden; }

.org-sidebar {
  width: 180px; flex-shrink: 0; background: #fff;
  border-right: 1px solid #e5e7eb;
  display: flex; flex-direction: column; padding: 12px; overflow-y: auto; gap: 12px;
}
.sidebar-title { font-size: 13px; font-weight: 700; color: #1a1a2e; }
.sidebar-section { display: flex; flex-direction: column; gap: 4px; }
.section-label { font-size: 11px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; }
.unassigned-list { display: flex; flex-direction: column; gap: 4px; max-height: 300px; overflow-y: auto; }
.bot-chip {
  display: flex; align-items: center; gap: 6px; padding: 5px 8px;
  background: #f3f4f6; border-radius: 8px; cursor: grab; font-size: 12px;
  border: 1px solid #e5e7eb; transition: background 0.15s;
  &:hover { background: #e0e7ff; border-color: #a5b4fc; }
}
.bot-chip-name { color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.no-unassigned { font-size: 11px; color: #9ca3af; text-align: center; padding: 8px 0; }
.sidebar-tips { margin-top: auto; font-size: 11px; color: #9ca3af; line-height: 1.8; }

.org-canvas-wrap { flex: 1; position: relative; overflow: hidden; }
.org-canvas { width: 100%; height: 100%; }
.canvas-empty {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  text-align: center; color: #9ca3af; font-size: 13px; pointer-events: none;
}

.ctx-menu {
  position: fixed; background: #fff; border: 1px solid #e5e7eb;
  border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 9999; min-width: 140px; overflow: hidden;
}
.ctx-item { padding: 9px 14px; font-size: 13px; color: #374151; cursor: pointer; &:hover { background: #f3f4f6; } }
.ctx-danger { color: #ef4444; &:hover { background: #fef2f2; } }
</style>
